import moment from 'moment';
import { STATUS_ORDER, STATUS_LABEL, activeTotal } from '../../utils/memberStatus';

// Each signup sits in exactly ONE current status, so the seven statuses below
// always sum to Signed Up (names from utils/memberStatus.js). "Active" is NOT a
// status: it is the total Approved + Hidden (every completed profile that is
// still up) — shown as its own number, never as a status-bar segment, or it
// would double count. "Completed" is the umbrella = everyone who finished the
// signup journey = Signed Up − Ghost = approved + hidden + inactive +
// deactivated + suspended + deleted; it is DERIVED (see completedOf) too.
export const COLORS = {
  signed: '#171717',
  completed: '#2563EB', // umbrella "finished signup" stage in the funnel
  active: '#15803D', // derived total Approved + Hidden
  approved: '#16A34A', // visible on search right now (the good current state)
  hidden: '#EF4444',
  inactive: '#F97316',
  ghost: '#F59E0B',
  deactivated: '#A855F7',
  suspended: '#0D9488',
  deleted: '#64748B',
};

// The seven mutually-exclusive current statuses, in status-bar order. They
// always sum to Signed Up.
export const STATES = STATUS_ORDER.map((key) => [key, STATUS_LABEL[key], COLORS[key]]);

export const STATE_KEYS = ['signed', 'active', ...STATUS_ORDER];

// `active` is carried as the server sends it (= approved + hidden) so summed
// rows keep it; read the Active number through activeTotal (utils/memberStatus).
export const EMPTY_ROW = STATE_KEYS.reduce((acc, k) => ({ ...acc, [k]: 0 }), {});

export const pct = (n, d) => (d ? Math.round((n / d) * 100) : 0);

/**
 * Largest-remainder (Hamilton) rounding. Given a map of parts and a total,
 * returns integer percentages keyed the same way that sum to exactly the
 * rounded whole (100% when the parts sum to the total). Avoids the "sums to
 * 99% / 101%" artifact you get from rounding each share independently.
 */
export const pctShares = (parts, total) => {
  const keys = Object.keys(parts);
  if (!total) return keys.reduce((acc, k) => ({ ...acc, [k]: 0 }), {});

  const exact = keys.map((k) => {
    const v = ((parts[k] || 0) / total) * 100;
    return { k, floor: Math.floor(v), rem: v - Math.floor(v), v };
  });

  const out = {};
  exact.forEach((e) => {
    out[e.k] = e.floor;
  });

  const target = Math.round(exact.reduce((s, e) => s + e.v, 0));
  let remaining = target - exact.reduce((s, e) => s + e.floor, 0);

  // Hand the leftover points to the largest fractional remainders first.
  [...exact]
    .sort((a, b) => b.rem - a.rem)
    .forEach((e) => {
      if (remaining > 0) {
        out[e.k] += 1;
        remaining -= 1;
      }
    });

  return out;
};

export const fmt = (n) => (n || 0).toLocaleString();

// "Completed" (umbrella) = finished the signup journey = Signed Up − Ghost
// = approved + hidden + inactive + deactivated + suspended + deleted.
export const completedOf = (r) => Math.max(0, (r.signed || 0) - (r.ghost || 0));

// Value of a metric for a row, used by the geographic charts/tables.
// "completion" = the active rate (% who became Active = Approved + Hidden);
// "completed" = the umbrella count; "active" = the derived total; everything
// else is a stored disjoint-status count.
export const metricVal = (r, m) => {
  if (m === 'completion') return pct(activeTotal(r), r.signed || 0);
  if (m === 'completed') return completedOf(r);
  if (m === 'active') return activeTotal(r);
  return r[m] || 0;
};

// Sum a list of state rows into a single totals row.
export const sumRows = (rows = []) =>
  rows.reduce((acc, r) => {
    Object.keys(EMPTY_ROW).forEach((k) => {
      acc[k] += r[k] || 0;
    });
    return acc;
  }, { ...EMPTY_ROW });

// Geolocation reports Delhi and its satellite cities as separate places, but for
// the signup geo breakdown they read as one metro — the Delhi NCR. These are the
// spellings the IP-geo source emits (core Delhi + the NCR districts). Matched on
// the lowercased, trimmed city name; add spellings here as new ones show up.
export const NCR_CITIES = new Set([
  'delhi', 'new delhi', 'delhi division', 'new delhi district',
  'central delhi', 'north delhi', 'south delhi', 'east delhi', 'west delhi',
  'north east delhi', 'north west delhi', 'south east delhi', 'south west delhi', 'shahdara',
  'gurugram', 'gurgaon', 'manesar',
  'noida', 'greater noida', 'gautam buddha nagar',
  'ghaziabad', 'loni', 'hapur',
  'faridabad', 'ballabgarh', 'ballabhgarh',
  'sonipat', 'sonepat', 'bahadurgarh', 'jhajjar', 'rohtak', 'rewari', 'panipat', 'bhiwadi',
]);

// Collapse every NCR-area city into a single aggregated "Delhi NCR" row, summing
// each state count, and drop the individual NCR rows. Non-NCR cities pass through
// unchanged. The merged row keeps the same shape (city/country + state counts) so
// the geo table and charts render it like any other row. Order doesn't matter —
// callers sort by `signed`.
export const groupNcrCities = (rows = []) => {
  const ncr = { ...EMPTY_ROW, city: 'Delhi NCR', country: 'India' };
  const rest = [];
  let matched = 0;
  rows.forEach((r) => {
    const name = String(r.city || '').trim().toLowerCase();
    if (NCR_CITIES.has(name)) {
      Object.keys(EMPTY_ROW).forEach((k) => { ncr[k] += r[k] || 0; });
      matched += 1;
    } else {
      rest.push(r);
    }
  });
  return matched ? [ncr, ...rest] : rest;
};

// Color for a completion-rate value (good / mid / bad), mirroring the HTML.
export const compColor = (p) => (p >= 20 ? COLORS.approved : p >= 12 ? COLORS.ghost : COLORS.hidden);

/**
 * Bucket the daily byDate rows into daily / weekly / monthly periods for the
 * trend + stage-rate charts. Each output row keeps all state counts plus a label.
 */
export const bucketByPeriod = (byDate = [], period = 'daily') => {
  if (!byDate.length) return [];

  const keyFor = (m) => {
    if (period === 'weekly') return m.clone().startOf('isoWeek').format('YYYY-MM-DD');
    if (period === 'monthly') return m.clone().startOf('month').format('YYYY-MM');
    return m.format('YYYY-MM-DD');
  };
  const labelFor = (m) => {
    if (period === 'weekly') return m.clone().startOf('isoWeek').format('MMM D');
    if (period === 'monthly') return m.format('MMM YYYY');
    return m.format('MMM D');
  };

  const map = new Map();
  byDate.forEach((r) => {
    const m = moment(r.date, 'YYYY-MM-DD');
    if (!m.isValid()) return;
    const key = keyFor(m);
    if (!map.has(key)) {
      map.set(key, { key, label: labelFor(m), ...EMPTY_ROW });
    }
    const bucket = map.get(key);
    Object.keys(EMPTY_ROW).forEach((k) => {
      bucket[k] += r[k] || 0;
    });
  });

  return Array.from(map.values()).sort((a, b) => (a.key < b.key ? -1 : 1));
};
